import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useStatistics from "datasets/hooks/useStatistics";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { BookPlus, Copy, Pencil } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { mutate as globalMutate } from "swr";
import { DropdownMenuItem } from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import useToast from "~/components/ui/toast";
import { useFavorites } from "~/hooks/useFavorites";
import { AddToProjectDialog } from "~/pages/manifold-nextgen/components/AddToProjectDialog";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import {
  EntityOverviewHeader,
  OverviewTile,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { OverflowMenu } from "~/pages/manifold-nextgen/components/OverflowMenu";
import { AccessLevelBadge } from "~/pages/manifold-nextgen/components/datasets/AccessLevelBadge";
import { DatasetEditDialog } from "~/pages/manifold-nextgen/components/datasets/DatasetEditDialog";
import { DatasetIcon } from "~/pages/manifold-nextgen/components/datasets/DatasetIcon";
import { useCanUseProjects } from "~/pages/manifold-nextgen/hooks/useCanUseProjects";
import { useCopyToClipboard } from "~/pages/manifold-nextgen/hooks/useCopyToClipboard";
import { useDatasetShare } from "~/pages/manifold-nextgen/hooks/useDatasetShare";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import { addProjectDataset, projectDatasetsPath } from "~/services/projects";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

interface DatasetOverviewProps {
  // Overrides the route-fetched dataset — used when this tab is rendered
  // outside the DatasetDetail route tree (the project context sidebar).
  dataset?: Dataset;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
}

export const DatasetOverview = ({
  dataset: datasetProp,
  sessionSidebarView = false,
}: DatasetOverviewProps = {}) => {
  const { data: fetchedDataset, isLoading, mutate } = useDataset(datasetProp?.id);
  const dataset = datasetProp ?? fetchedDataset;
  const { data: stats, isLoading: loadingStats } = useStatistics(dataset?.id);
  const hasEditPermission = useDatasetPermission("edit:catalog-dataset", dataset?.id);
  const canEdit =
    !sessionSidebarView && dataset?.access_level === DatasetAccessLevel.OWNER && hasEditPermission;
  const canUseProjects = useCanUseProjects();
  const canAddToProject = !sessionSidebarView && canUseProjects;
  const [editOpen, setEditOpen] = useState(false);
  const [addToProjectOpen, setAddToProjectOpen] = useState(false);
  const { collaborators, accessLevels, collaboratorsError } = useDatasetShare(
    dataset?.id,
    !sessionSidebarView
  );
  const { showError } = useToast();
  const copyToClipboard = useCopyToClipboard();
  const { isFavorite, toggleFavorite } = useFavorites(
    "dataset-favorites",
    dataset ? [dataset] : [],
    (d) => d.id
  );

  const handleAttachToProject = async (projectId: string) => {
    if (!dataset) {
      return;
    }
    await addProjectDataset(projectId, dataset.id);
    await globalMutate(projectDatasetsPath(projectId));
  };

  const handleSaveDescription = async (next: string) => {
    if (!dataset) {
      return;
    }
    try {
      await apiMutate<Dataset>(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        description: next,
      });
      await mutate();
    } catch (e) {
      showError("Failed to update dataset description");
      throw e;
    }
  };

  const owners = useOwners(
    collaborators,
    accessLevels,
    DatasetAccessLevel.OWNER,
    collaboratorsError
  );

  if (!datasetProp && isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading overview"
        className="mx-auto flex w-full max-w-3xl flex-col gap-4"
      >
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!dataset) return null;

  const tiles: OverviewTile[] = [
    {
      label: "Role",
      content: <AccessLevelBadge accessLevel={dataset.access_level} />,
    },
    ...(sessionSidebarView
      ? []
      : [
          {
            label: "Owners",
            content: <AvatarStack items={owners} emptyLabel="—" />,
          },
        ]),
    {
      label: "Tables",
      content: dataset.table_count,
    },
    {
      label: "Data Size",
      content: loadingStats ? (
        <Skeleton className="h-4 w-16" />
      ) : stats?.table_total_bytes !== undefined ? (
        prettyBytes(stats.table_total_bytes)
      ) : (
        "—"
      ),
    },
  ];

  // The sidebar shows the tiles alone — the page it sits beside already names
  // the dataset and carries its controls.
  const header = sessionSidebarView
    ? {}
    : {
        icon: <DatasetIcon dataset={dataset} size={36} />,
        title: dataset.title,
        type: "Dataset",
        updatedAt: dataset.updated_at,
        actions: (
          <>
            <FavoriteButton
              favorite={isFavorite(dataset.id)}
              onToggle={() => toggleFavorite(dataset.id)}
              outlined
            />
            <OverflowMenu label="dataset actions">
              {canEdit && (
                <DropdownMenuItem onSelect={() => setEditOpen(true)}>
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit name and appearance
                </DropdownMenuItem>
              )}
              {canAddToProject && (
                <DropdownMenuItem onSelect={() => setAddToProjectOpen(true)}>
                  <BookPlus className="size-4" aria-hidden="true" />
                  Add to project
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => void copyToClipboard(dataset.id, "Dataset ID")}>
                <Copy className="size-4" aria-hidden="true" />
                Copy dataset ID
              </DropdownMenuItem>
            </OverflowMenu>
          </>
        ),
      };

  return (
    <div className="mx-auto w-full max-w-3xl pb-12 pt-4">
      <EntityOverviewHeader
        tiles={tiles}
        tileLayout={sessionSidebarView ? "stacked" : "strip"}
        {...header}
      >
        <EditableDescription
          heading="Description"
          value={dataset.description}
          canEdit={canEdit}
          onSave={handleSaveDescription}
        />
      </EntityOverviewHeader>
      {canEdit && editOpen && (
        <DatasetEditDialog dataset={dataset} open onOpenChange={setEditOpen} />
      )}
      {canAddToProject && (
        <AddToProjectDialog
          open={addToProjectOpen}
          onOpenChange={setAddToProjectOpen}
          entityName={dataset.title}
          isAttached={(project) => project.dataset_ids.includes(dataset.id)}
          attach={handleAttachToProject}
        />
      )}
    </div>
  );
};
